import Grid from '@app/components/Grid';
import { Card, Colors, FormGroup, InputGroup, MenuItem } from '@blueprintjs/core';
import { AiImageCategory } from 'dy-frontend-http-repository/lib/modules/AiImage/enums';
import React, { useEffect, useState } from 'react';
import PromptOverview from '../../PromptOverview';
import { configuration } from './consts';
import { IllustrationDesignCategoryPromptKeys } from './types';
import Select from '@components/Select';

interface IllustrationDesignGeneratorProps {
    isGenerating: boolean;
    onPromptChange: (prompt: string | null) => void;
}

type Props = IllustrationDesignGeneratorProps;

const IllustrationDesignGenerator: React.FC<Props> = ({ isGenerating, onPromptChange }) => {
    const [focusedInputKey, setFocusedInputKey] = useState<string | null>(null);
    const [subject, setSubject] = useState('');
    const [setting, setSetting] = useState('on white background');
    const [style, setStyle] = useState('realistic');
    const [surface, setSurface] = useState('digital');
    const [medium, setMedium] = useState('paper');
    const [vibe, setVibe] = useState('plain');
    const [color, setColor] = useState('bright');

    const getCategoryPromptKeys = (): IllustrationDesignCategoryPromptKeys => {
        const overviewRendererProps: IllustrationDesignCategoryPromptKeys = {
            subject: { isActive: false, key: '', text: '' },
            color: { isActive: false, key: '', text: '' },
            medium: { isActive: false, key: '', text: '' },
            setting: { isActive: false, key: '', text: '' },
            style: { isActive: false, key: '', text: '' },
            surface: { isActive: false, key: '', text: '' },
            vibe: { isActive: false, key: '', text: '' },
        };

        for (const inputKey of Object.keys(configuration.inputs)) {
            // Prompt property not used in overview
            if (!configuration.inputs[inputKey].isUsedInPromptOverview) {
                continue;
            }

            const value = promptPropertiesData[inputKey].value;
            const elementKey = `${AiImageCategory.PHOTO}_${inputKey}`;

            // No input is focused
            if (focusedInputKey === null) {
                overviewRendererProps[inputKey] = {
                    key: elementKey,
                    text: value,
                    isActive: false,
                };
                continue;
            }

            // Input for property "key" is focused
            if (focusedInputKey === inputKey) {
                overviewRendererProps[inputKey] = {
                    key: elementKey,
                    text: value,
                    isActive: true,
                };
                continue;
            }

            overviewRendererProps[inputKey] = {
                key: elementKey,
                text: value,
                isActive: false,
            };
        }

        return overviewRendererProps;
    };

    useEffect(() => {
        if (subject.trim().length === 0) {
            onPromptChange(null);
            return;
        }

        onPromptChange(configuration.getFinalPrompt(getCategoryPromptKeys()));
    }, [subject, setting, style, surface, medium, vibe, color]);

    const promptPropertiesData: {
        [key in string]: { value: string; dispatch: React.Dispatch<React.SetStateAction<string>> };
    } = {
        subject: { value: subject, dispatch: setSubject },
        setting: { value: setting, dispatch: setSetting },
        style: { value: style, dispatch: setStyle },
        surface: { value: surface, dispatch: setSurface },
        medium: { value: medium, dispatch: setMedium },
        vibe: { value: vibe, dispatch: setVibe },
        color: { value: color, dispatch: setColor },
    };

    const renderPrompt = () => {
        return (
            <Card compact style={{ background: Colors.DARK_GRAY2 }}>
                <PromptOverview
                    template={configuration.promptTemplate}
                    categoryPromptConfiguration={getCategoryPromptKeys()}
                />
            </Card>
        );
    };

    const renderGeneratorFields = () => {
        const filterItemOption = (query: string, item: string) => {
            return item.toLowerCase().indexOf(query.toLowerCase()) >= 0;
        };

        const renderCreateItemOption = (
            query: string,
            active: boolean,
            handleClick: React.MouseEventHandler<HTMLElement>
        ) => {
            return (
                <MenuItem
                    icon="add"
                    text={`${query}`}
                    active={active}
                    onClick={handleClick}
                    shouldDismissPopover={false}
                />
            );
        };

        return (
            <Grid container>
                {Object.entries(configuration.inputs).map(([key, promptPropertyInputConfiguration]) => {
                    if (promptPropertyInputConfiguration.inputType === 'input') {
                        return (
                            <Grid lg={6}>
                                <FormGroup label={promptPropertyInputConfiguration.label}>
                                    <InputGroup
                                        disabled={isGenerating}
                                        placeholder={promptPropertyInputConfiguration.placeholder}
                                        value={promptPropertiesData[key].value}
                                        onChange={(e) => promptPropertiesData[key].dispatch(e.target.value)}
                                        onFocus={() => setFocusedInputKey(key)}
                                    />
                                </FormGroup>
                            </Grid>
                        );
                    } else {
                        let items: string[] = promptPropertyInputConfiguration.defaultOptions;
                        const canAddCustomOption = promptPropertyInputConfiguration.isCustomOptionAllowed;

                        const renderItemOption = (item: string, { handleClick }) => {
                            return (
                                <MenuItem
                                    key={item}
                                    text={item}
                                    onClick={handleClick}
                                    active={item === promptPropertiesData[key].value}
                                />
                            );
                        };

                        return (
                            <Grid lg={6}>
                                <FormGroup label={promptPropertyInputConfiguration.label}>
                                    <Select<string>
                                        fill
                                        filterable
                                        key={key}
                                        items={items}
                                        disabled={isGenerating}
                                        itemPredicate={filterItemOption}
                                        createNewItemFromQuery={canAddCustomOption ? (query) => query : undefined}
                                        createNewItemRenderer={canAddCustomOption ? renderCreateItemOption : undefined}
                                        itemRenderer={renderItemOption}
                                        onItemSelect={(item) => promptPropertiesData[key].dispatch(item)}
                                        popoverProps={{
                                            onOpening: () => setFocusedInputKey(key),
                                        }}
                                        inputProps={{
                                            placeholder: 'Add keywords / Search',
                                        }}
                                        selectButtonProps={{
                                            fill: true,
                                            text: promptPropertiesData[key].value,
                                            rightIcon: 'double-caret-vertical',
                                        }}
                                    />
                                </FormGroup>
                            </Grid>
                        );
                    }
                })}
            </Grid>
        );
    };

    return (
        <div>
            <div className="mb-2">{renderPrompt()}</div>
            <div>{renderGeneratorFields()}</div>
        </div>
    );
};

export default IllustrationDesignGenerator;
